import { useCallback, useEffect, useRef, useState } from "react";
import { search } from "@/api/addressBook.service";
import styles from "./EntitySearchPicker.module.css";

type EntityType = "firm" | "lawyer";

type EntitySearchResult = {
  id: string;
  name: string;
  subtitle: string;
  officeId?: string;
  contactId?: string;
  parentId?: string;
  wpTermId?: string;
};

type EntitySearchPickerProps = {
  type: EntityType;
  label: string;
  onSelect: (entity: EntitySearchResult) => void;
};

async function apiSearch(query: string, type: EntityType): Promise<EntitySearchResult[]> {
  if (!query.trim()) return [];

  if (type === "firm") {
    const result = await search.companies(query);
    return result.companies.map(c => ({
      id: c.id,
      name: c.name,
      subtitle: c.offices?.[0]?.city ?? "",
      officeId: c.offices?.[0]?.officeId?.toString(),
    }));
  }

  const result = await search.contacts(query);
  return result.items.map(c => ({
    id: c.contact_address_book_id,
    name: c.insert,
    subtitle: c.display.split(" > ").slice(1).join(" > "),
    contactId: c.contact_address_book_id,
    parentId: c.firm_address_book_id,
    officeId: c.office_id ?? undefined,
    wpTermId: /^\d+$/.test(c.term_id) ? c.term_id : undefined,
  }));
}

function SearchIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function Spinner() {
  return (
    <svg
      className={styles.spinner}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.25" />
      <path
        d="M12 2a10 10 0 0 1 10 10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function EntitySearchPicker({ type, label, onSelect }: EntitySearchPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<EntitySearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const placeholder = type === "firm" ? "Search firms..." : "Search lawyers...";

  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
    setResults([]);
    setActiveIndex(-1);
  }, []);

  useEffect(() => {
    if (!open) return;

    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close();
      }
    }

    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") {
        close();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open, close]);

  useEffect(() => {
    if (!open) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    setLoading(true);
    debounceRef.current = setTimeout(() => {
      apiSearch(query, type)
        .then(items => {
          setResults(items);
          setActiveIndex(-1);
        })
        .catch(err => {
          console.error("Search failed:", err);
          setResults([]);
        })
        .finally(() => {
          setLoading(false);
        });
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, type, open]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const item = listRef.current.children[activeIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!results.length && !loading) return;

    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        setActiveIndex(prev => (prev < results.length - 1 ? prev + 1 : 0));
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        setActiveIndex(prev => (prev > 0 ? prev - 1 : results.length - 1));
        break;
      }
      case "Enter": {
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < results.length) {
          onSelect(results[activeIndex]);
          close();
        }
        break;
      }
    }
  }

  function handleSelect(entity: EntitySearchResult) {
    onSelect(entity);
    close();
  }

  const listboxId = `entity-search-listbox-${type}`;

  return (
    <div className={styles.container} ref={containerRef}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen(prev => !prev)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        {label}
      </button>

      {open && (
        <div className={styles.dropdown} role="dialog" aria-label={placeholder}>
          <div className={styles.searchRow}>
            <span className={styles.searchIcon}>
              <SearchIcon />
            </span>
            <input
              ref={inputRef}
              type="text"
              className={styles.searchInput}
              placeholder={placeholder}
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              role="combobox"
              aria-expanded={results.length > 0}
              aria-controls={listboxId}
              aria-activedescendant={
                activeIndex >= 0 ? `entity-option-${type}-${activeIndex}` : undefined
              }
              aria-autocomplete="list"
            />
            {loading && (
              <span className={styles.loadingIcon}>
                <Spinner />
              </span>
            )}
          </div>

          <ul
            ref={listRef}
            id={listboxId}
            className={styles.resultsList}
            role="listbox"
            aria-label={`${type === "firm" ? "Firm" : "Lawyer"} results`}
          >
            {loading && results.length === 0 ? (
              <li className={styles.emptyState} role="option" aria-selected={false} aria-disabled>
                <Spinner /> Searching...
              </li>
            ) : results.length === 0 ? (
              <li className={styles.emptyState} role="option" aria-selected={false} aria-disabled>
                No results found
              </li>
            ) : (
              results.map((entity, index) => (
                <li
                  key={entity.id}
                  id={`entity-option-${type}-${index}`}
                  role="option"
                  aria-selected={index === activeIndex}
                  className={`${styles.resultItem} ${index === activeIndex ? styles.resultItemActive : ""}`}
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => handleSelect(entity)}
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  <span className={styles.resultName}>{entity.name}</span>
                  <span className={styles.resultSubtitle}>{entity.subtitle}</span>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export type { EntityType, EntitySearchResult, EntitySearchPickerProps };
